import { Box, Breakpoint, Dialog, DialogTitle, IconButton, SxProps } from "@mui/material";
import { FC } from "react";
import { useTranslation } from "../../../explorer/hooks/useTranslation";

import { AppIconsClose } from "../AppIcons";
import {
    StyledDialogActions,
    TextConfirmButtonStyled,
} from "./CommonDialog.style";
import CommonDialogSubTitle from "./CommonDialogSubTitle";

type TCommonDialog = {
    open: boolean;
    closeDialog: () => void;
    maxWidth?: Breakpoint | false;
    fullScreen?: boolean;
    dialogTitleLabel?: string;
    dialogExtraTitleLabel?: string;
    dialogContentLabel?: string;
    useSubTitle?: boolean;
    useTitle?: boolean;
    children?: JSX.Element;
    confirmButton?: () => void;
    denyButton?: () => void;
    useDenyBtn?: boolean;
    useConfirmBtn?: boolean;
    disableDenyBtn?: boolean;
    disableConfirmBtn?: boolean;
    denyButtonText?: string;
    confirmButtonText?: string;
    MoreComponent?: React.ComponentType;
    sx?: SxProps;
};

const CommonDialog: FC<TCommonDialog> = (props) => {
    const {
        open,
        closeDialog,
        maxWidth = "sm",
        fullScreen = false,
        dialogTitleLabel,
        dialogExtraTitleLabel,
        dialogContentLabel,
        useSubTitle = false,
        children,
        confirmButton,
        denyButton,
        useDenyBtn = true,
        useConfirmBtn = true,
        disableDenyBtn = false,
        disableConfirmBtn = false,
        denyButtonText = "common.disagree",
        confirmButtonText = "common.agree",
        MoreComponent,
        sx,
    } = props;
    const { t } = useTranslation();

    return (
        <Dialog
          open={open}
          onClose={closeDialog}
          maxWidth={maxWidth}
          fullWidth
          fullScreen={fullScreen}
          sx={sx}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: dialogTitleLabel
                        ? "space-between"
                        : "flex-end",
                }}
            >
                {dialogTitleLabel && t(dialogTitleLabel)}
                {dialogExtraTitleLabel && dialogExtraTitleLabel}
                {MoreComponent ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginLeft: "auto" }}>
                      <MoreComponent />
                      <IconButton onClick={closeDialog}>
                          <AppIconsClose />
                      </IconButton>
                  </Box>
                ) : (
                    <IconButton onClick={closeDialog} sx={{ marginLeft: "auto" }}>
                        <AppIconsClose />
                    </IconButton>
                )}
            </DialogTitle>

            <CommonDialogSubTitle
                useSubTitle={useSubTitle}
                dialogContentLabel={dialogContentLabel}
                children={children}
            />

            {(useDenyBtn || useConfirmBtn) && (
                <StyledDialogActions>
                    {useDenyBtn && (
                        // Deny button
                        <TextConfirmButtonStyled
                            color="secondary"
                            onClick={denyButton ?? closeDialog}
                            disabled={disableDenyBtn}
                        >
                            {t(denyButtonText)}
                        </TextConfirmButtonStyled>
                    )}

                    {useConfirmBtn && (
                        // Confirm button
                        <TextConfirmButtonStyled
                            variant="contained"
                            onClick={confirmButton}
                            color="primary"
                            disabled={disableConfirmBtn}
                        >
                            {t(confirmButtonText)}
                        </TextConfirmButtonStyled>
                    )}
                </StyledDialogActions>
            )}
        </Dialog>
    );
};

export default CommonDialog;
